import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { AppConstants } from 'src/app/commons/app-constants';

/** Shapes of the screen support API (ScreenSupportDtos on the backend). */
export type SupportSessionStatus = 'REQUESTED' | 'INVITED' | 'ACTIVE' | 'ENDED' | 'DECLINED' | 'EXPIRED';
export type SupportCompanyState = 'DEFAULT' | 'SELF_REQUEST' | 'BLOCKED';

export interface SupportSession {
  id: number;
  companyId: number;
  companyName?: string | null;
  customerUserId: number;
  customerName?: string | null;
  customerEmail?: string | null;
  ticketId?: number | null;
  ticketNumber?: string | null;
  origin: 'STAFF' | 'CUSTOMER';
  staffName?: string | null;
  staffEmail?: string | null;
  status: SupportSessionStatus;
  note?: string | null;
  createdAt: string;
  expiresAt: string;
  activatedAt?: string | null;
  endedAt?: string | null;
  endedBy?: string | null;
  customerJoinedAt?: string | null;
  staffJoinedAt?: string | null;
  mine: boolean;
  canJoin: boolean;
}

export interface SupportStatus {
  enabled: boolean;
  canRequest: boolean;
  session: SupportSession | null;
}

export interface SupportCompanyRow {
  companyId: number;
  tenantId: number;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  users: number;
  state: SupportCompanyState;
  note?: string | null;
  updatedBy?: string | null;
  lastSessionAt?: string | null;
}

export interface SupportPersonRow {
  id: number;
  name: string;
  email?: string | null;
  mobile?: string | null;
  admin: boolean;
}

export interface SupportPage<T> {
  content: T[];
  totalElements: number;
  number: number;
  size: number;
}

export interface SupportMaster {
  enabled: boolean;
  configured: boolean;
}

/** Customer side (/api/v1/screen-support) and support side (/api/v1/admin/screen-support) of screen support. */
@Injectable({ providedIn: 'root' })
export class ScreenSupportService {

  private readonly CUSTOMER = `${AppConstants.API_URL}api/v1/screen-support`;
  private readonly STAFF = `${AppConstants.API_URL}api/v1/admin/screen-support`;
  private readonly opts = { withCredentials: true } as const;

  /** The session waiting for this user (set by the prompt and the ticket card). */
  readonly myStatus$ = new BehaviorSubject<SupportStatus | null>(null);

  constructor(private http: HttpClient) {}

  // ── customer ──
  status(): Observable<SupportStatus> {
    return this.http.get<SupportStatus>(`${this.CUSTOMER}/status`, this.opts).pipe(tap((s) => this.myStatus$.next(s)));
  }
  forTicket(ticketId: number): Observable<SupportStatus> {
    return this.http.get<SupportStatus>(`${this.CUSTOMER}/ticket/${ticketId}`, this.opts);
  }
  request(body: { ticketId?: number | null; note?: string }): Observable<SupportSession> {
    return this.http.post<SupportSession>(`${this.CUSTOMER}/request`, body, this.opts);
  }
  join(id: number): Observable<{ joinUrl: string }> {
    return this.http.post<{ joinUrl: string }>(`${this.CUSTOMER}/${id}/join`, {}, this.opts);
  }
  decline(id: number): Observable<SupportSession> {
    return this.http.post<SupportSession>(`${this.CUSTOMER}/${id}/decline`, {}, this.opts);
  }
  end(id: number): Observable<SupportSession> {
    return this.http.post<SupportSession>(`${this.CUSTOMER}/${id}/end`, {}, this.opts);
  }

  // ── support staff ──
  staffForTicket(ticketId: number): Observable<SupportSession | null> {
    return this.http.get<SupportSession | null>(`${this.STAFF}/ticket/${ticketId}`, this.opts);
  }
  staffSessions(status: string, q: string, page: number, size: number): Observable<SupportPage<SupportSession>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (status) params = params.set('status', status);
    if (q.trim()) params = params.set('q', q.trim());
    return this.http.get<SupportPage<SupportSession>>(`${this.STAFF}/sessions`, { ...this.opts, params });
  }
  staffStart(body: { ticketId?: number | null; companyId?: number | null; customerUserId?: number | null; note?: string }): Observable<SupportSession> {
    return this.http.post<SupportSession>(`${this.STAFF}/start`, body, this.opts);
  }
  staffAccept(id: number): Observable<SupportSession> {
    return this.http.post<SupportSession>(`${this.STAFF}/${id}/accept`, {}, this.opts);
  }
  staffJoin(id: number): Observable<{ joinUrl: string }> {
    return this.http.post<{ joinUrl: string }>(`${this.STAFF}/${id}/join`, {}, this.opts);
  }
  staffEnd(id: number): Observable<SupportSession> {
    return this.http.post<SupportSession>(`${this.STAFF}/${id}/end`, {}, this.opts);
  }
  companies(q: string, state: string, page: number, size: number): Observable<SupportPage<SupportCompanyRow>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (q.trim()) params = params.set('q', q.trim());
    if (state) params = params.set('state', state);
    return this.http.get<SupportPage<SupportCompanyRow>>(`${this.STAFF}/companies`, { ...this.opts, params });
  }
  people(companyId: number, q: string): Observable<SupportPersonRow[]> {
    let params = new HttpParams();
    if (q.trim()) params = params.set('q', q.trim());
    return this.http.get<SupportPersonRow[]>(`${this.STAFF}/companies/${companyId}/people`, { ...this.opts, params });
  }
  setState(tenantId: number, state: SupportCompanyState, note?: string): Observable<void> {
    return this.http.put<void>(`${this.STAFF}/companies/${tenantId}/state`, { state, note }, this.opts);
  }
  setStates(tenantIds: number[], state: SupportCompanyState, note?: string): Observable<{ changed: number }> {
    return this.http.put<{ changed: number }>(`${this.STAFF}/companies/state`, { tenantIds, state, note }, this.opts);
  }
  master(): Observable<SupportMaster> {
    return this.http.get<SupportMaster>(`${this.STAFF}/settings`, this.opts);
  }
  saveMaster(enabled: boolean): Observable<SupportMaster> {
    return this.http.put<SupportMaster>(`${this.STAFF}/settings`, { enabled }, this.opts);
  }
}
